import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import {
  BOOKING_OFFER_SERVICE_OPTIONS,
  BookingOfferConfig,
  BookingOfferPopupDisplayMode,
  BookingOfferServiceType,
  BookingOfferTier,
  uploadBookingOfferPopupImage,
  upsertBookingOfferConfig,
} from '../../../services/MasterAdmin/bookingOfferService';
import { fetchDeepLinkRoutes } from '../../../services/MasterAdmin/deepLinkService';
import type { DeepLinkRoute } from '../../../models/MasterAdmin/deepLink';

const DEFAULT_TIERS: BookingOfferTier[] = [
  { bookingNumber: 1, discountPercent: 20, title: '', description: '' },
  { bookingNumber: 2, discountPercent: 15, title: '', description: '' },
  { bookingNumber: 3, discountPercent: 10, title: '', description: '' },
];

const inputClassName =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:[color-scheme:dark]';

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  initial: BookingOfferConfig | null;
  usedServiceTypes: BookingOfferServiceType[];
  onClose: () => void;
  onSaved: () => void;
};

const BookingOfferFormModal: React.FC<Props> = ({
  open,
  mode,
  initial,
  usedServiceTypes,
  onClose,
  onSaved,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [serviceType, setServiceType] = useState<BookingOfferServiceType>('AMBULANCE');
  const [isActive, setIsActive] = useState(true);
  const [tiers, setTiers] = useState<BookingOfferTier[]>(DEFAULT_TIERS);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [marketingTitle, setMarketingTitle] = useState('');
  const [popupDescription, setPopupDescription] = useState('');
  const [popupImageUrl, setPopupImageUrl] = useState('');
  const [popupDisplayMode, setPopupDisplayMode] =
    useState<BookingOfferPopupDisplayMode>('BOTH');
  const [showOnSplash, setShowOnSplash] = useState(false);
  const [splashDisplayMode, setSplashDisplayMode] =
    useState<BookingOfferPopupDisplayMode>('BOTH');
  const [splashImageUrl, setSplashImageUrl] = useState('');
  const [splashBackgroundColor, setSplashBackgroundColor] = useState('#0EBEAE');
  const [ctaDeepLinkRouteId, setCtaDeepLinkRouteId] = useState('');
  const [deepLinkRoutes, setDeepLinkRoutes] = useState<DeepLinkRoute[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [splashImageFile, setSplashImageFile] = useState<File | null>(null);
  const [splashImagePreview, setSplashImagePreview] = useState<string | null>(null);
  const splashFileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tierPopupFiles, setTierPopupFiles] = useState<Record<number, File | null>>({});
  const [tierSplashFiles, setTierSplashFiles] = useState<Record<number, File | null>>({});
  const [tierPopupPreviews, setTierPopupPreviews] = useState<Record<number, string>>({});
  const [tierSplashPreviews, setTierSplashPreviews] = useState<Record<number, string>>({});
  const tierPopupInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const tierSplashInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const availableServiceTypes = useMemo(() => {
    if (mode === 'edit' && initial) {
      return BOOKING_OFFER_SERVICE_OPTIONS.filter((o) => o.value === initial.serviceType);
    }
    return BOOKING_OFFER_SERVICE_OPTIONS;
  }, [initial, mode]);

  useEffect(() => {
    if (!open) return;
    fetchDeepLinkRoutes(false)
      .then(setDeepLinkRoutes)
      .catch(() => setDeepLinkRoutes([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && initial) {
      setServiceType(initial.serviceType);
      setIsActive(Boolean(initial.isActive));
      setTiers(
        Array.isArray(initial.tiers) && initial.tiers.length ? initial.tiers : DEFAULT_TIERS
      );
      setValidFrom(initial.validFrom ? initial.validFrom.slice(0, 16) : '');
      setValidUntil(initial.validUntil ? initial.validUntil.slice(0, 16) : '');
      setMarketingTitle(initial.marketingTitle || '');
      setPopupDescription(initial.popupDescription || '');
      setPopupImageUrl(initial.popupImageUrl || '');
      setPopupDisplayMode(initial.popupDisplayMode || 'BOTH');
      setShowOnSplash(Boolean(initial.showOnSplash));
      setSplashDisplayMode(initial.splashDisplayMode || initial.popupDisplayMode || 'BOTH');
      setSplashImageUrl(initial.splashImageUrl || '');
      setSplashBackgroundColor(initial.splashBackgroundColor || '#0EBEAE');
      setCtaDeepLinkRouteId(initial.ctaDeepLinkRouteId || '');
    } else {
      const firstAvailable = availableServiceTypes[0]?.value || 'AMBULANCE';
      setServiceType(firstAvailable);
      setIsActive(true);
      setTiers(DEFAULT_TIERS);
      setValidFrom('');
      setValidUntil('');
      setMarketingTitle('');
      setPopupDescription('');
      setPopupImageUrl('');
      setPopupDisplayMode('BOTH');
      setShowOnSplash(false);
      setSplashDisplayMode('BOTH');
      setSplashImageUrl('');
      setSplashBackgroundColor('#0EBEAE');
      setCtaDeepLinkRouteId('');
    }

    setImageFile(null);
    setImagePreview(null);
    setSplashImageFile(null);
    setSplashImagePreview(null);
    setTierPopupFiles({});
    setTierSplashFiles({});
    setTierPopupPreviews({});
    setTierSplashPreviews({});
    setError(null);
  }, [open, mode, initial, availableServiceTypes]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(popupImageUrl || null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile, popupImageUrl]);

  useEffect(() => {
    if (!splashImageFile) {
      setSplashImagePreview(splashImageUrl || popupImageUrl || null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(splashImageFile);
    setSplashImagePreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [splashImageFile, splashImageUrl, popupImageUrl]);

  const routeOptions = useMemo(
    () =>
      [...deepLinkRoutes]
        .filter((r) => r.isActive)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label)),
    [deepLinkRoutes]
  );

  if (!open) return null;

  const handleTierChange = (index: number, discountPercent: string) => {
    setTiers((prev) =>
      prev.map((tier, i) =>
        i === index
          ? { ...tier, discountPercent: Math.max(0, Number(discountPercent) || 0) }
          : tier
      )
    );
  };

  const handleTierFieldChange = (
    index: number,
    field: 'title' | 'description' | 'ctaDeepLinkRouteId' | 'popupImageUrl' | 'splashImageUrl',
    value: string
  ) => {
    setTiers((prev) =>
      prev.map((tier, i) =>
        i === index
          ? {
              ...tier,
              [field]:
                field === 'ctaDeepLinkRouteId' || field === 'popupImageUrl' || field === 'splashImageUrl'
                  ? value || null
                  : value,
            }
          : tier
      )
    );
  };

  const handleTierPopupImagePick = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid tier banner image.');
      return;
    }
    setTierPopupFiles((prev) => ({ ...prev, [index]: file }));
    const objectUrl = URL.createObjectURL(file);
    setTierPopupPreviews((prev) => {
      if (prev[index]) URL.revokeObjectURL(prev[index]);
      return { ...prev, [index]: objectUrl };
    });
    setError(null);
  };

  const handleTierSplashImagePick = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid tier splash image.');
      return;
    }
    setTierSplashFiles((prev) => ({ ...prev, [index]: file }));
    const objectUrl = URL.createObjectURL(file);
    setTierSplashPreviews((prev) => {
      if (prev[index]) URL.revokeObjectURL(prev[index]);
      return { ...prev, [index]: objectUrl };
    });
    setError(null);
  };

  const handleRemoveTierPopupImage = (index: number) => {
    setTierPopupFiles((prev) => ({ ...prev, [index]: null }));
    setTierPopupPreviews((prev) => {
      if (prev[index]) URL.revokeObjectURL(prev[index]);
      const next = { ...prev };
      delete next[index];
      return next;
    });
    handleTierFieldChange(index, 'popupImageUrl', '');
    const input = tierPopupInputRefs.current[index];
    if (input) input.value = '';
  };

  const handleRemoveTierSplashImage = (index: number) => {
    setTierSplashFiles((prev) => ({ ...prev, [index]: null }));
    setTierSplashPreviews((prev) => {
      if (prev[index]) URL.revokeObjectURL(prev[index]);
      const next = { ...prev };
      delete next[index];
      return next;
    });
    handleTierFieldChange(index, 'splashImageUrl', '');
    const input = tierSplashInputRefs.current[index];
    if (input) input.value = '';
  };

  const tierPopupPreview = (index: number, tier: BookingOfferTier) =>
    tierPopupPreviews[index] || tier.popupImageUrl || null;

  const tierSplashPreview = (index: number, tier: BookingOfferTier) =>
    tierSplashPreviews[index] || tier.splashImageUrl || tier.popupImageUrl || null;

  const renderRouteSelect = (
    value: string,
    onChange: (next: string) => void,
    placeholder = 'Default for service type'
  ) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full min-w-[10rem] rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
    >
      <option value="">{placeholder}</option>
      {routeOptions.map((route) => (
        <option key={route.id} value={route.id}>
          {route.label} ({route.slug})
        </option>
      ))}
    </select>
  );

  const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setImageFile(file);
    setError(null);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setPopupImageUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSplashImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid splash image file.');
      return;
    }
    setSplashImageFile(file);
    setError(null);
  };

  const handleRemoveSplashImage = () => {
    setSplashImageFile(null);
    setSplashImageUrl('');
    if (splashFileInputRef.current) splashFileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);

      let finalImageUrl = popupImageUrl || null;
      if (imageFile) {
        setUploadingImage(true);
        finalImageUrl = await uploadBookingOfferPopupImage(imageFile);
        setUploadingImage(false);
      }

      let finalSplashImageUrl = splashImageUrl || null;
      if (splashImageFile) {
        setUploadingImage(true);
        finalSplashImageUrl = await uploadBookingOfferPopupImage(splashImageFile);
        setUploadingImage(false);
      }

      setUploadingImage(true);
      const tiersWithImages = await Promise.all(
        tiers.map(async (tier, index) => {
          let popupImageUrl = tier.popupImageUrl || null;
          let splashImageUrlTier = tier.splashImageUrl || null;
          const popupFile = tierPopupFiles[index];
          const splashFile = tierSplashFiles[index];
          if (popupFile) {
            popupImageUrl = await uploadBookingOfferPopupImage(popupFile);
          }
          if (splashFile) {
            splashImageUrlTier = await uploadBookingOfferPopupImage(splashFile);
          }
          return {
            ...tier,
            popupImageUrl,
            splashImageUrl: splashImageUrlTier,
          };
        })
      );
      setUploadingImage(false);

      await upsertBookingOfferConfig(serviceType, {
        isActive,
        tiers: tiersWithImages,
        marketingTitle: marketingTitle || null,
        popupDescription: popupDescription || null,
        popupImageUrl: finalImageUrl,
        popupDisplayMode,
        showOnSplash,
        splashDisplayMode,
        splashImageUrl: finalSplashImageUrl,
        splashBackgroundColor: splashBackgroundColor || null,
        ctaDeepLinkRouteId: ctaDeepLinkRouteId || null,
        validFrom: validFrom || null,
        validUntil: validUntil || null,
      });

      onSaved();
      onClose();
    } catch (err: any) {
      setUploadingImage(false);
      setError(err?.response?.data?.message || 'Failed to save offer');
    } finally {
      setSaving(false);
    }
  };

  const serviceLabel =
    serviceType === 'AMBULANCE' ? 'ambulance booking' : 'doctor consultation';

  const renderDisplayModeRadios = (
    name: string,
    value: BookingOfferPopupDisplayMode,
    onChange: (mode: BookingOfferPopupDisplayMode) => void
  ) => (
    <div className="flex flex-wrap gap-2">
      {(
        [
          ['IMAGE_ONLY', 'Image only'],
          ['TEXT_ONLY', 'Text only'],
          ['BOTH', 'Image + text'],
        ] as const
      ).map(([mode, label]) => (
        <label
          key={mode}
          className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs dark:border-gray-600"
        >
          <input
            type="radio"
            name={name}
            value={mode}
            checked={value === mode}
            onChange={() => onChange(mode)}
            className="text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-gray-800 dark:text-gray-100">{label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {mode === 'create' ? 'Create offer & discount' : 'Edit offer & discount'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {error ? (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Offer setup
                </h3>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Service type
                  </label>
                  <select
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value as BookingOfferServiceType)}
                    disabled={mode === 'edit'}
                    className={inputClassName}
                  >
                    {availableServiceTypes.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {mode === 'create' && usedServiceTypes.includes(serviceType) ? (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                      An offer already exists for this service. Saving will update that offer.
                    </p>
                  ) : null}
                </div>
                <label className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 px-3 py-2 dark:border-gray-600">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    Active
                  </span>
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Valid from (optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={validFrom}
                      onChange={(e) => setValidFrom(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Valid until (optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                </div>
              </section>

              <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Home popup (app & web)
                </h3>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Popup headline
                  </label>
                  <input
                    type="text"
                    value={marketingTitle}
                    onChange={(e) => setMarketingTitle(e.target.value)}
                    placeholder="GET 20% OFF"
                    className={inputClassName}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Popup description
                  </label>
                  <textarea
                    value={popupDescription}
                    onChange={(e) => setPopupDescription(e.target.value)}
                    rows={2}
                    className={`${inputClassName} resize-y`}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Default popup banner (fallback)
                  </label>
                  <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                    Used when a tier has no banner. Prefer uploading per tier below.
                  </p>
                  <div className="flex gap-3 rounded-lg border border-dashed border-gray-300 p-3 dark:border-gray-600">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Popup banner preview"
                        className="h-20 shrink-0 rounded-md object-cover"
                        style={{ aspectRatio: '352/170' }}
                      />
                    ) : (
                      <div
                        className="flex h-20 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-400 dark:bg-gray-800"
                        style={{ aspectRatio: '352/170' }}
                      >
                        <PhotoIcon className="h-8 w-8" />
                      </div>
                    )}
                    <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        2.07:1 landscape recommended (352x170)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                        >
                          {imagePreview ? 'Change' : 'Upload'}
                        </button>
                        {imagePreview ? (
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="rounded-md px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImagePick}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Popup display mode
                  </label>
                  {renderDisplayModeRadios('popupDisplayMode', popupDisplayMode, setPopupDisplayMode)}
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Tap popup navigates to
                  </label>
                  {renderRouteSelect(ctaDeepLinkRouteId, setCtaDeepLinkRouteId)}
                </div>
              </section>

              <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Splash screen
                  </h3>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={showOnSplash}
                      onChange={(e) => setShowOnSplash(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      Enabled
                    </span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Compact logo at top, offer content below on app launch.
                </p>

                {showOnSplash ? (
                  <div className="space-y-4">
                    <div>
                      <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        Splash display mode
                      </label>
                      {renderDisplayModeRadios(
                        'splashDisplayMode',
                        splashDisplayMode,
                        setSplashDisplayMode
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        Splash background color
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={splashBackgroundColor}
                          onChange={(e) =>
                            setSplashBackgroundColor(e.target.value.toUpperCase())
                          }
                          className="h-9 w-12 cursor-pointer rounded border border-gray-300 bg-white p-1 dark:border-gray-600"
                        />
                        <input
                          type="text"
                          value={splashBackgroundColor}
                          onChange={(e) => setSplashBackgroundColor(e.target.value)}
                          placeholder="#0EBEAE"
                          className={`${inputClassName} max-w-[9rem]`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                        Splash banner (optional)
                      </label>
                      <div className="flex gap-3 rounded-lg border border-dashed border-gray-300 p-3 dark:border-gray-600">
                        {splashImagePreview ? (
                          <img
                            src={splashImagePreview}
                            alt="Splash banner preview"
                            className="h-20 shrink-0 rounded-md object-cover"
                            style={{ aspectRatio: '352/170' }}
                          />
                        ) : (
                          <div
                            className="flex h-20 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-400 dark:bg-gray-800"
                            style={{ aspectRatio: '352/170' }}
                          >
                            <PhotoIcon className="h-8 w-8" />
                          </div>
                        )}
                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Uses popup image if empty (352x170 recommended)
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => splashFileInputRef.current?.click()}
                              className="rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                            >
                              {splashImagePreview ? 'Change' : 'Upload'}
                            </button>
                            {splashImageUrl || splashImageFile ? (
                              <button
                                type="button"
                                onClick={handleRemoveSplashImage}
                                className="rounded-md px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                Remove
                              </button>
                            ) : null}
                          </div>
                        </div>
                        <input
                          ref={splashFileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleSplashImagePick}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    Enable to show this offer on the app splash screen.
                  </p>
                )}
              </section>
            </div>

            <div className="mt-4">
              <h3 className="mb-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
                Discount tiers — {serviceLabel}
              </h3>
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                Upload a banner for each booking tier (1st = new user, 2nd, 3rd). App and web show
                the image matching the user&apos;s next eligible discount.
              </p>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {tiers.map((tier, index) => {
                  const popupPreview = tierPopupPreview(index, tier);
                  const splashPreview = tierSplashPreview(index, tier);
                  const tierLabel =
                    tier.bookingNumber === 1
                      ? '1st booking (new user)'
                      : tier.bookingNumber === 2
                        ? '2nd booking'
                        : tier.bookingNumber === 3
                          ? '3rd booking'
                          : `${tier.bookingNumber}th booking`;

                  return (
                    <div
                      key={tier.bookingNumber}
                      className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {tierLabel}
                          </p>
                          <p className="text-xs text-gray-500">Booking #{tier.bookingNumber}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={0.5}
                            value={tier.discountPercent}
                            onChange={(e) => handleTierChange(index, e.target.value)}
                            className="w-16 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          />
                          <span className="text-xs text-gray-500">%</span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                          Popup / offer banner (2.07:1 - 352x170)
                        </label>
                        <div className="flex gap-2 rounded-lg border border-dashed border-gray-300 p-2 dark:border-gray-600">
                          {popupPreview ? (
                            <img
                              src={popupPreview}
                              alt={`Tier ${tier.bookingNumber} banner`}
                              className="h-16 shrink-0 rounded object-cover"
                              style={{ aspectRatio: '352/170' }}
                            />
                          ) : (
                            <div
                              className="flex h-16 shrink-0 items-center justify-center rounded bg-gray-50 text-gray-400 dark:bg-gray-800"
                              style={{ aspectRatio: '352/170' }}
                            >
                              <PhotoIcon className="h-6 w-6" />
                            </div>
                          )}
                          <div className="flex flex-col justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => tierPopupInputRefs.current[index]?.click()}
                              className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100"
                            >
                              Upload
                            </button>
                            {popupPreview ? (
                              <button
                                type="button"
                                onClick={() => handleRemoveTierPopupImage(index)}
                                className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                Remove
                              </button>
                            ) : null}
                          </div>
                          <input
                            ref={(el) => {
                              tierPopupInputRefs.current[index] = el;
                            }}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleTierPopupImagePick(index, e)}
                          />
                        </div>
                      </div>

                      {showOnSplash ? (
                        <div className="mb-3">
                          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Splash banner (optional - 352x170)
                          </label>
                          <div className="flex gap-2 rounded-lg border border-dashed border-gray-300 p-2 dark:border-gray-600">
                            {splashPreview ? (
                              <img
                                src={splashPreview}
                                alt={`Tier ${tier.bookingNumber} splash`}
                                className="h-16 shrink-0 rounded object-cover"
                                style={{ aspectRatio: '352/170' }}
                              />
                            ) : (
                              <div
                                className="flex h-16 shrink-0 items-center justify-center rounded bg-gray-50 text-gray-400 dark:bg-gray-800"
                                style={{ aspectRatio: '352/170' }}
                              >
                                <PhotoIcon className="h-6 w-6" />
                              </div>
                            )}
                            <div className="flex flex-col justify-center gap-1">
                              <button
                                type="button"
                                onClick={() => tierSplashInputRefs.current[index]?.click()}
                                className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100"
                              >
                                Upload
                              </button>
                              {tier.splashImageUrl || tierSplashFiles[index] ? (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTierSplashImage(index)}
                                  className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                  Remove
                                </button>
                              ) : null}
                            </div>
                            <input
                              ref={(el) => {
                                tierSplashInputRefs.current[index] = el;
                              }}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleTierSplashImagePick(index, e)}
                            />
                          </div>
                        </div>
                      ) : null}

                      <div className="space-y-2">
                        <input
                          type="text"
                          value={tier.title || ''}
                          onChange={(e) =>
                            handleTierFieldChange(index, 'title', e.target.value)
                          }
                          placeholder="Tier headline (optional)"
                          className={inputClassName}
                        />
                        <textarea
                          value={tier.description || ''}
                          onChange={(e) =>
                            handleTierFieldChange(index, 'description', e.target.value)
                          }
                          rows={2}
                          placeholder="Tier description (optional)"
                          className={`${inputClassName} resize-y`}
                        />
                        <div>
                          <label className="mb-1 block text-xs text-gray-500">Navigate to</label>
                          {renderRouteSelect(
                            tier.ctaDeepLinkRouteId || '',
                            (next) => handleTierFieldChange(index, 'ctaDeepLinkRouteId', next),
                            'Use offer default'
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploadingImage}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {(saving || uploadingImage) && <Loader2 className="h-4 w-4 animate-spin" />}
              {uploadingImage ? 'Uploading image…' : mode === 'create' ? 'Create offer' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingOfferFormModal;
